import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { useMemo } from 'react';
import { format } from 'date-fns';
import Decimal from 'decimal.js';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ConditionalWrapper, Spinner, Typography, WithInfoIcon } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import GenericError from '~/components/GenericError/GenericError';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { SalesSummaryStatus } from '~/models';
import { salesSummaryTextsPath } from '~/models/SalesSummaryModel';
import { toDatestampString } from '~/models/time';
import { selectors } from '~/store/state-selectors';
import { utils } from '~/utils';
import { getLastXMonths } from '~/utils/date.utils';
import { getXAxisTextForQuarterlyDate } from '../charts/charts.utils';
import { commonBarProps, commonCartesianGridProps, commonXAxisProps } from '../charts/commonChartProps';
import CustomBarShape from '../charts/CustomBarShape/CustomBarShape';
import CustomLegend from '../charts/CustomLegend/CustomLegend';
import CustomTooltip from '../charts/CustomTooltip/CustomTooltip';
import CustomXAxisTick from '../charts/CustomXAxisTick/CustomXAxisTick';
import GraphWidgetBottomCard from '../GraphWidget/GraphWidgetBottomCard';
import GraphWidgetLayout from '../GraphWidget/GraphWidgetLayout';
import GraphWidgetTopCard from '../GraphWidget/GraphWidgetTopCard';
import { useSalesSummary } from './hooks/useSalesSummary';
import styles from './SalesWidget.module.scss';
export const salesWidgetTextsPath = 'pages.dashboard.salesWidget';
const statusesOrder = [
    SalesSummaryStatus.Approved,
    SalesSummaryStatus.Reviewed,
    SalesSummaryStatus.Sent,
    SalesSummaryStatus.Expired,
    SalesSummaryStatus.Canceled,
    SalesSummaryStatus.Terminated
];
const getStatusToText = (texts)=>({
        [SalesSummaryStatus.Approved]: texts.statusLabels.signed(),
        [SalesSummaryStatus.Reviewed]: texts.statusLabels.reviewed(),
        [SalesSummaryStatus.Sent]: texts.statusLabels.notReviewed(),
        [SalesSummaryStatus.Canceled]: texts.statusLabels.withdrawn(),
        [SalesSummaryStatus.Terminated]: texts.statusLabels.terminated(),
        [SalesSummaryStatus.Expired]: texts.statusLabels.expired()
    });
const statusToColor = {
    [SalesSummaryStatus.Approved]: colors.blue700,
    [SalesSummaryStatus.Reviewed]: colors.purple700,
    [SalesSummaryStatus.Sent]: colors.purple300,
    [SalesSummaryStatus.Canceled]: colors.dark200,
    [SalesSummaryStatus.Terminated]: colors.red500,
    [SalesSummaryStatus.Expired]: colors.purple50
};
const renderBar = (status)=>/*#__PURE__*/ _createElement(Bar, _object_spread_props(_object_spread({}, commonBarProps), {
        key: status,
        dataKey: (item)=>item.statuses[status].total.toNumber(),
        stackId: "a",
        fill: statusToColor[status],
        shape: /*#__PURE__*/ _jsx(CustomBarShape, {
            getHasRadius: (item)=>{
                const statusIndex = statusesOrder.indexOf(status);
                const statusesAbove = statusesOrder.slice(statusIndex + 1);
                return !statusesAbove.some((s)=>item.statuses[s].total.gt(0));
            },
            getTestId: (item)=>"".concat(status, "-for-").concat(toDatestampString(item.date))
        })
    }));
export function RenderTooltip(_0) {
    let { testId } = _0, additionalProps = _object_without_properties(_0, [
        "testId"
    ]);
    const { t: salesWidgetTexts } = useLocaleKeys(salesWidgetTextsPath);
    const statusToText = getStatusToText(salesWidgetTexts);
    return /*#__PURE__*/ _jsx(CustomTooltip, _object_spread_props(_object_spread({}, additionalProps), {
        skin: "dark",
        testId: "".concat(testId, "-tooltip"),
        getTitle: (label, payload)=>{
            if (!payload || !payload.length) return '';
            const item = payload[0].payload;
            return format(item.date, 'MMMM');
        },
        getItems: (payload)=>{
            if (!payload || !payload.length) return;
            const item = payload[0].payload;
            return statusesOrder.filter((s)=>item.statuses[s].total.gt(0)).map((s)=>({
                    id: s,
                    label: "".concat(utils.formatNumberWithCommas(item.statuses[s].total), " ").concat(statusToText[s]),
                    color: statusToColor[s]
                }));
        },
        getBody: (payload)=>{
            if (!payload || !payload.length) return null;
            const item = payload[0].payload;
            const totalProposals = Object.values(item.statuses).reduce((acc, val)=>acc.add(val.total), new Decimal(0)).toNumber();
            return salesWidgetTexts.outOfProposalsSent({
                count: totalProposals,
                countText: totalProposals.toString()
            });
        }
    }));
}
export function getLegendItems(data, texts) {
    const statusToText = getStatusToText(texts);
    return statusesOrder.filter((status)=>data.some((item)=>item.statuses[status].total.gt(0))).map((status)=>({
            status,
            label: statusToText[status],
            color: statusToColor[status]
        }));
}
function SalesWidgetChart(param) {
    let { testId, data, isTestMode } = param;
    const { t: salesWidgetTexts } = useLocaleKeys(salesWidgetTextsPath);
    const barChartProps = isTestMode ? {
        height: 100,
        width: 100
    } : {};
    const legendItems = useMemo(()=>getLegendItems(data, salesWidgetTexts), [
        data
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": testId,
        className: styles.chart,
        children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
            condition: !isTestMode,
            wrapper: (children)=>/*#__PURE__*/ _jsx(ResponsiveContainer, {
                    minHeight: 300,
                    className: styles.container,
                    children: children
                }),
            children: /*#__PURE__*/ _jsxs(BarChart, _object_spread_props(_object_spread({}, barChartProps), {
                data: data,
                barCategoryGap: "10%",
                margin: {
                    top: 20,
                    left: 16,
                    right: 16
                },
                children: [
                    /*#__PURE__*/ _jsx(CartesianGrid, _object_spread({}, commonCartesianGridProps)),
                    /*#__PURE__*/ _jsx(Legend, {
                        content: /*#__PURE__*/ _jsx(CustomLegend, {
                            testId: "".concat(testId, "-legend"),
                            items: legendItems,
                            gap: 16
                        })
                    }),
                    /*#__PURE__*/ _jsx(XAxis, _object_spread_props(_object_spread({}, commonXAxisProps), {
                        dataKey: (item)=>item.date,
                        height: 49,
                        tick: /*#__PURE__*/ _jsx(CustomXAxisTick, {
                            tickHeight: 30,
                            getText: getXAxisTextForQuarterlyDate
                        })
                    })),
                    /*#__PURE__*/ _jsx(YAxis, {
                        axisLine: false,
                        tickLine: false,
                        width: 32,
                        tickFormatter: (value)=>value === 0 ? '' : utils.formatNumberWithCommas(value),
                        fontSize: 12
                    }),
                    /*#__PURE__*/ _jsx(Tooltip, {
                        cursor: false,
                        isAnimationActive: false,
                        content: /*#__PURE__*/ _jsx(RenderTooltip, {
                            testId: testId
                        })
                    }),
                    statusesOrder.map(renderBar)
                ]
            }))
        })
    });
}
const TopCard = (param)=>{
    let { label, info, value, loading, testId, linkTo } = param;
    return /*#__PURE__*/ _jsx(GraphWidgetTopCard, {
        testId: testId,
        linkTo: linkTo,
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.topCard,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsx(WithInfoIcon, {
                        info: info,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            children: label
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h5",
                    weight: "normal",
                    testId: "".concat(testId, "-value"),
                    children: loading ? /*#__PURE__*/ _jsx(Spinner, {
                        size: 30,
                        testId: "".concat(testId, "-loading")
                    }) : value
                })
            ]
        })
    });
};
function getMockChartData(now) {
    return getLastXMonths(now, 12).map((date)=>({
            date,
            statuses: {
                [SalesSummaryStatus.Sent]: {
                    total: new Decimal(2)
                },
                [SalesSummaryStatus.Approved]: {
                    total: new Decimal(3)
                },
                [SalesSummaryStatus.Terminated]: {
                    total: new Decimal(0)
                },
                [SalesSummaryStatus.Canceled]: {
                    total: new Decimal(1)
                },
                [SalesSummaryStatus.Reviewed]: {
                    total: new Decimal(1)
                },
                [SalesSummaryStatus.Expired]: {
                    total: new Decimal(0)
                }
            }
        }));
}
function MockSalesWidgetChart(param) {
    let { isTestMode } = param;
    const { getNow } = useIoContext();
    const data = useMemo(()=>getMockChartData(getNow()), []);
    return /*#__PURE__*/ _jsx(SalesWidgetChart, {
        data: data,
        testId: "sales-widget-mock-chart",
        isTestMode: isTestMode
    });
}
function SalesWidgetContent(param) {
    let { loading, data, isTestMode } = param;
    const hasSentAProposal = useStore(selectors.hasSentAProposal);
    const { t: salesWidgetTexts } = useLocaleKeys(salesWidgetTextsPath);
    const { t: salesSummaryTexts } = useLocaleKeys(salesSummaryTextsPath);
    const areCardsLoading = !data;
    return /*#__PURE__*/ _jsxs(GraphWidgetLayout, {
        title: salesWidgetTexts.title(),
        loading: loading,
        bottomHeight: "l",
        testId: "sales-widget",
        children: [
            /*#__PURE__*/ _jsx(TopCard, {
                loading: areCardsLoading,
                value: data ? data.formattedNumProposalsSentThisYear() : '',
                testId: "sales-widget-proposals-sent-this-year",
                label: salesWidgetTexts.proposalsSentThisYear.label(),
                info: salesWidgetTexts.proposalsSentThisYear.info(),
                linkTo: routes.proposalsReport
            }),
            /*#__PURE__*/ _jsx(TopCard, {
                loading: areCardsLoading,
                value: data ? data.formattedMedianTimeToSign(salesSummaryTexts) : '',
                testId: "sales-widget-median-time-to-sign",
                label: salesWidgetTexts.medianTimeToSign.label(),
                info: salesWidgetTexts.medianTimeToSign.info()
            }),
            /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                testId: "monthly-sales".concat(areCardsLoading ? '-loading' : ''),
                loading: areCardsLoading,
                loadingMsg: salesWidgetTexts.loadingMsg(),
                empty: data && data.isEmpty() && !hasSentAProposal,
                emptyMsg: salesWidgetTexts.emptyMsg(),
                loadingOrEmptyBg: ()=>/*#__PURE__*/ _jsx(MockSalesWidgetChart, {
                        isTestMode: isTestMode
                    }),
                children: data && /*#__PURE__*/ _jsx(SalesWidgetChart, {
                    data: data.calendar(),
                    testId: "sales-widget-chart",
                    isTestMode: isTestMode
                })
            })
        ]
    });
}
export default function SalesWidget(param) {
    let { isTestMode } = param;
    const { salesSummary, isLoading, isFetching, error } = useSalesSummary({
        refreshOnHookMount: true
    });
    if (error) return /*#__PURE__*/ _jsx(GenericError, {
        testId: "widget-error"
    });
    if (isLoading) return /*#__PURE__*/ _jsx(SalesWidgetContent, {
        loading: true,
        isTestMode: isTestMode
    });
    return /*#__PURE__*/ _jsx(SalesWidgetContent, {
        loading: isFetching,
        data: salesSummary,
        isTestMode: isTestMode
    });
}
